import React, { useState } from 'react';
import { ContactEditContent } from '../components/features/contacts/ContactEditContent';
import { useLayoutConfig } from '../hooks/useLayoutConfig';
import type { Contact } from '../types/contact.types';

/**
 * /contact-preview — local visual QA page for the contact view.
 *
 * Renders ContactEditContent with a fully-populated mock contact so layout
 * issues, edge cases, and dark-mode behavior can be checked without touching
 * a real org. Five preset scenarios cover the most-stressed states:
 *   1. Hot lead — handed off, every field populated
 *   2. Engaged buyer — flywheel enrolled, no handoff
 *   3. Closed client — assigned agent, past appointment
 *   4. AI paused — manual hold, with reason
 *   5. Minimal — almost-empty contact (sparsity test)
 *
 * Note: this page bypasses the live GQL fetch by passing a full Contact
 * directly to ContactEditContent. Child tabs that GQL-fetch by contactId
 * will render empty (preview-mock-* IDs don't resolve), but the hero +
 * Suggested Status + Remi-design tabs all derive from the prop and look
 * correct.
 */

const BASE_ADDRESS = {
  street: '142 Maple St',
  city: 'Boston',
  state: 'MA',
  zip: '02116',
};

// Example notes + propertyEnrichment shape — surfaced by NotesTab and
// HomeownerDataPanel via `contact as MappedContact`. Live tabs that fetch
// via GraphQL (calls / emails / website / saved-properties / valuations /
// activity feed) don't read the contact prop, so they render empty in the
// preview without Apollo MockedProvider.
const PREVIEW_NOTES = {
  items: [
    {
      noteId: 'note-1',
      subject: 'Saturday tour confirmed',
      body:
        '<p>Tour locked in for Saturday 11am at 142 Maple. Pull comps for 89 Elm same day. They asked specifically about school district and walking distance to the Orange Line.</p>',
      authorId: 'agent-sarah',
      authorName: 'Sarah Mendoza-Patel',
      authorType: 'agent',
      noteType: 'handoff',
      isPinned: true,
      category: 'tour',
      tags: ['tour-scheduled'],
      createdAt: '2026-05-15T16:30:00Z',
      updatedAt: '2026-05-15T16:30:00Z',
    },
    {
      noteId: 'note-2',
      subject: 'Flood-zone disclosure sent',
      body:
        '<p>Lead asked about flood-zone disclosure for 142 Maple — sent FEMA map. Property is in Zone X (minimal risk). Lead confirmed comfort with the disclosure.</p>',
      authorId: 'agent-david',
      authorName: 'David Park',
      authorType: 'agent',
      noteType: 'note',
      isPinned: false,
      category: 'property',
      tags: ['disclosure'],
      createdAt: '2026-05-13T10:15:00Z',
      updatedAt: '2026-05-13T10:15:00Z',
    },
    {
      noteId: 'note-3',
      subject: 'Preapproval received',
      body:
        '<p>Preapproval letter received from Rocket Mortgage at $725k. Lead has 20% down ready and a flexible close window (June 30 – July 30).</p>',
      authorId: 'remi',
      authorName: 'Remi',
      authorType: 'assistant',
      noteType: 'note',
      isPinned: false,
      category: 'financing',
      tags: ['preapproval'],
      createdAt: '2026-05-10T14:08:00Z',
      updatedAt: '2026-05-10T14:08:00Z',
    },
    {
      noteId: 'note-4',
      subject: 'First voice contact · qualifier complete',
      body:
        '<p>Remi reached the lead on call #2 — 8 min conversation. Confirmed buyer intent, $650-750k budget, Boston (south end / Brookline / Cambridge preferred), 2br minimum, parking required.</p>',
      authorId: 'remi',
      authorName: 'Remi',
      authorType: 'assistant',
      noteType: 'note',
      isPinned: false,
      category: 'qualifier',
      tags: ['intent-confirmed'],
      createdAt: '2026-05-07T11:42:00Z',
      updatedAt: '2026-05-07T11:42:00Z',
    },
  ],
  total: 4,
  hasMore: false,
};

const PREVIEW_PROPERTY_ENRICHMENT = {
  items: [
    {
      enrichmentId: 'pe-1',
      updatedAt: '2026-05-15T08:00:00Z',
      processedData: JSON.stringify({
        propertyDetails: {
          estimatedValue: 845000,
          yearBuilt: 1928,
          propertyType: 'Single Family · Victorian',
          bedrooms: 4,
          bathrooms: 2.5,
          squareFeet: 2240,
          lotSquareFeet: 4350,
          lastSaleDate: '2017-08-14',
          lastSaleAmount: 562000,
          floodZone: 'X',
          floodZoneDescription: 'Minimal flood hazard',
          ownerOccupied: true,
          estimatedEquity: 318000,
          equityPercent: 56,
          neighborhood: { name: 'South End' },
        },
      }),
    },
  ],
  total: 1,
  hasMore: false,
};

const FULL_CONTACT_BASE: Contact = {
  id: 'preview-mock-jamie-chen',
  organization_id: 'preview-org',
  firstName: 'Jamie',
  lastName: 'Chen',
  phoneNumber: '+16175550148',
  phoneStatus: 'active',
  read: true,
  email: 'jamie.chen@example.com',
  email_status: 'active',
  address: BASE_ADDRESS,
  createdAt: '2026-04-01T12:00:00Z',
  updatedAt: '2026-05-15T16:30:00Z',
  opt_in: true,
  opt_in_date: '2026-04-01',
  opt_in_desc: 'https://example.com/zillow-lead-capture',
  is_test_contact: false,
  call_tester: false,
  status: 'active',
  lifecycleStage: 'engaged',
  recentOutcome: 'appointment_set',
  lastOutcomeChannel: 'sms',
  lastCallAnswered: '2026-05-12T14:22:00Z',
  source: 'Zillow',
  leadSource: 'Zillow',
  leadSourceSecondary: 'PPC',
  leadIntent: 'Buyer',
  score: 87,
  previousScore: 70,
  engagementScore: 82,
  contactHealth: 90,
  readinessScore: 75,
  readinessBand: 'high',
  websiteActivitySource: 'idx',
  valuationRequested: false,
  tags: ['hot-lead', 'first-time-buyer', 'south-end', 'preapproved'],
  claimedLead: { name: 'Sarah Mendoza', phoneNumber: '+16175550199' },
  claimedLeadSource: 'manual',
  assignedAgentId: 'agent-sarah',
  assignedAgent: {
    agentId: 'agent-sarah',
    firstName: 'Sarah',
    lastName: 'Mendoza',
    email: 'sarah@mendozagroup.com',
    phone: '+16175550199',
    role: 'Lead Agent',
    status: 'active',
  },
  firebaseUid: undefined,
  crmSource: 'followupboss',
  crmID: 'fub-123456',
  crmSynced: true,
  aiPaused: true,
  aiPausedAt: '2026-05-14T18:00:00Z',
  aiPausedReason: 'Lead requested a 48-hour pause while reviewing two competing offers; resume after Saturday tour.',
  flywheelEnrolled: true,
  flywheelOptOut: false,
  has_ealerts_registered: true,
  ealerts_delivery_health: 'good',
  ealerts_delivery_status: 'active',
  ealerts_last_delivery_status: 'delivered',
  ealerts_health_status: 'healthy',
  ealerts_last_delivery_at: '2026-05-16T09:00:00Z',
  metadata: undefined,
  customFields: undefined,
  handoffNotes: [
    {
      addedBy: 'agent-sarah',
      addedByName: 'Sarah Mendoza',
      note: 'Tour locked in for Saturday 11am at 142 Maple. Pull comps for 89 Elm same day.',
      timestamp: '2026-05-15T16:30:00Z',
    },
  ],
  activeHandoffId: 'handoff-mock-001',
  // Cast-through nested fields the tabs read via `contact as MappedContact`.
  // Lets NotesTab + Property Insights render examples without Apollo mocking.
  ...({ notes: PREVIEW_NOTES, propertyEnrichment: PREVIEW_PROPERTY_ENRICHMENT } as unknown as Partial<Contact>),
};

type Scenario =
  | 'everything'
  | 'paused'
  | 'non-flywheel'
  | 'minimal'
  | 'no-summary'
  | 'flywheel-with-plan'
  | 'flywheel-no-plan'
  | 'long-opt-in';

// Sample 5-step follow-up plan — exercises the FutureOutreachPlanCard
// stretched out (so we can confirm the activity feed below it still has
// breathing room inside the 680px right-column cap).
const SAMPLE_FOLLOW_UP_PLAN = {
  hasActiveFlow: true,
  steps: [
    {
      stepId: 'step-1',
      channel: 'sms',
      when: 'In 2 hours',
      title: 'Confirm Saturday tour time',
      purpose: 'Lock in 11am vs. 12pm — they hinted at brunch plans on call #3.',
      status: 'queued',
    },
    {
      stepId: 'step-2',
      channel: 'call',
      when: 'Tomorrow · 10am',
      title: 'Walk through pre-approval next steps',
      purpose: 'Rocket Mortgage letter expires 6/30 — make sure they know.',
      status: 'queued',
    },
    {
      stepId: 'step-3',
      channel: 'email',
      when: 'Friday · 2pm',
      title: 'Send comp report for 89 Elm',
      purpose: 'They asked for parallel listings in the same zip with parking.',
      status: 'queued',
    },
    {
      stepId: 'step-4',
      channel: 'sms',
      when: 'Saturday · 9am',
      title: 'Day-of tour reminder',
      purpose: 'Confirm address + parking, share the agent intro card.',
      status: 'queued',
    },
    {
      stepId: 'step-5',
      channel: 'call',
      when: 'Sunday · 6pm',
      title: 'Post-tour debrief',
      purpose: 'Capture sentiment + start drafting offer if ready.',
      status: 'queued',
    },
  ],
};

const SCENARIOS: { id: Scenario; label: string; description: string; build: () => Contact }[] = [
  {
    id: 'everything',
    label: 'Everything · max signals',
    description:
      'Every field populated — score 94 + claimed by agent + AI paused + opted in + CRM-synced + long agent name + tags + handoff notes. Note: claimed and "flywheel enrolled" are mutually exclusive in the UI; this scenario shows the claimed banner alongside the paused banner.',
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-everything',
      firstName: 'Alessandra',
      lastName: 'Hawthorne',
      email: 'alessandra.hawthorne@longtailexample.com',
      phoneNumber: '+16175550148',
      score: 94,
      previousScore: 78,
      engagementScore: 88,
      contactHealth: 92,
      readinessScore: 90,
      lifecycleStage: 'hot',
      recentOutcome: 'appointment_set',
      lastOutcomeChannel: 'sms',
      leadIntent: 'Buyer',
      leadSource: 'Zillow',
      leadSourceSecondary: 'PPC',
      tags: [
        'hot-lead',
        'preapproved',
        'first-time-buyer',
        'south-end',
        'school-district',
        'flood-zone-aware',
      ],
      address: { street: '142 Maple Street', city: 'Boston', state: 'MA', zip: '02116' },
      opt_in: true,
      opt_in_date: '2026-04-01',
      opt_in_desc: 'https://example.com/zillow-lead-capture-form-with-utm-params',
      crmSynced: true,
      crmSource: 'followupboss',
      crmID: 'fub-9876543',
      claimedLead: {
        name: 'Sarah Mendoza-Patel',
        phoneNumber: '+16175550199',
      },
      claimedLeadSource: 'manual',
      assignedAgentId: 'agent-sarah',
      assignedAgent: {
        agentId: 'agent-sarah',
        firstName: 'Sarah',
        lastName: 'Mendoza-Patel',
        email: 'sarah.mendoza-patel@mendozagroup.com',
        phone: '+16175550199',
        role: 'Lead Agent',
        status: 'active',
      },
      activeHandoffId: 'handoff-mock-001',
      flywheelEnrolled: true,
      // Claimed banner fires when flywheelOptOut === true AND claimedLead
      // is populated. AI paused stacks alongside it.
      flywheelOptOut: true,
      aiPaused: true,
      aiPausedAt: '2026-05-14T18:00:00Z',
      aiPausedReason:
        'Lead requested a 48-hour pause while reviewing two competing offers; resume after Saturday tour.',
      has_ealerts_registered: true,
      ealerts_delivery_health: 'good',
      ealerts_delivery_status: 'active',
      handoffNotes: [
        {
          addedBy: 'agent-sarah',
          addedByName: 'Sarah Mendoza-Patel',
          note: 'Tour locked in for Saturday 11am at 142 Maple. Pull comps for 89 Elm same day.',
          timestamp: '2026-05-15T16:30:00Z',
        },
        {
          addedBy: 'agent-david',
          addedByName: 'David Park',
          note: 'Lead asked about flood-zone disclosure — sent FEMA map.',
          timestamp: '2026-05-13T10:15:00Z',
        },
      ],
    }),
  },
  {
    id: 'non-flywheel',
    label: 'Non-flywheel · all data',
    description:
      "Every field populated EXCEPT flywheel — the org/contact is not enrolled in Remi's follow-up flywheel. The Suggested Contact Status card is hidden; everything else (hero, status banners w/o flywheel, side panel, tabs) renders normally.",
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-non-flywheel',
      firstName: 'Theo',
      lastName: 'Ramirez',
      email: 'theo.ramirez@example.com',
      phoneNumber: '+16175550166',
      score: 71,
      previousScore: 60,
      lifecycleStage: 'qualified',
      recentOutcome: 'follow_up',
      leadIntent: 'Buyer',
      leadSource: 'Realtor',
      leadSourceSecondary: 'Organic',
      tags: ['serious-buyer', 'cash-friendly', 'south-end', 'preapproved'],
      address: { street: '12 Walnut Ct', city: 'Cambridge', state: 'MA', zip: '02138' },
      crmSynced: true,
      crmSource: 'followupboss',
      crmID: 'fub-2025001',
      claimedLead: { name: 'Mia Chen', phoneNumber: '+16175550177' },
      claimedLeadSource: 'manual',
      assignedAgentId: 'agent-mia',
      assignedAgent: {
        agentId: 'agent-mia',
        firstName: 'Mia',
        lastName: 'Chen',
        email: 'mia.chen@mendozagroup.com',
        phone: '+16175550177',
        role: 'Agent',
        status: 'active',
      },
      // Flywheel OFF for this scenario:
      flywheelEnrolled: false,
      flywheelOptOut: undefined,
      activeHandoffId: null,
      aiPaused: false,
      handoffNotes: [],
    }),
  },
  {
    id: 'paused',
    label: 'AI paused · do not contact',
    description:
      'Outreach paused with a reason. Tests the "Do Not Contact" path + the amber "Remi is paused" banner.',
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-paused',
      firstName: 'Anna',
      lastName: "O'Brien",
      email: 'anna.obrien@example.com',
      phoneNumber: '+16175550122',
      score: 22,
      previousScore: 38,
      lifecycleStage: 'cold',
      recentOutcome: 'no_response',
      tags: ['cold', 'reactivation'],
      aiPaused: true,
      aiPausedAt: '2026-05-10T18:00:00Z',
      aiPausedReason: 'Lead asked us to stop contacting for 90 days while they sort out financing.',
      claimedLead: null,
      claimedLeadSource: null,
      assignedAgentId: null,
      assignedAgent: null,
      activeHandoffId: null,
      flywheelOptOut: false,
      handoffNotes: [],
    }),
  },
  {
    id: 'minimal',
    label: 'Sparse · missing fields',
    description:
      'Only name + phone — tests how the layout collapses when most fields are empty (no tags, no email, no address).',
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-minimal',
      firstName: 'Casey',
      lastName: '',
      email: '',
      email_status: undefined,
      phoneNumber: '+16175550100',
      address: { street: '', city: '', state: '', zip: '' },
      opt_in: false,
      opt_in_date: undefined,
      opt_in_desc: undefined,
      score: undefined,
      previousScore: undefined,
      leadSource: undefined,
      leadSourceSecondary: undefined,
      leadIntent: undefined,
      tags: [],
      claimedLead: null,
      claimedLeadSource: null,
      assignedAgentId: null,
      assignedAgent: null,
      activeHandoffId: null,
      crmSynced: false,
      crmID: undefined,
      crmSource: undefined,
      flywheelOptOut: undefined,
      flywheelEnrolled: false,
      has_ealerts_registered: false,
      handoffNotes: [],
      lifecycleStage: 'new',
      recentOutcome: undefined,
    }),
  },
  {
    id: 'no-summary',
    label: 'No summary · empty state',
    description:
      'Same Alessandra contact as the "Everything" scenario, but the AI summary field is missing — Current Status Summary card flips to its empty state with a Generate summary button.',
    build: () => {
      const base = SCENARIOS[0].build();
      return {
        ...base,
        id: 'preview-mock-no-summary',
        // Sentinel read by CurrentStatusSummary to force the empty state.
        // In production this'll be driven by the absence of an AI summary
        // field on the contact (backend ask #6).
        ...({ _previewEmptySummary: true } as unknown as Partial<Contact>),
      };
    },
  },
  {
    id: 'flywheel-with-plan',
    label: 'Flywheel · with plan',
    description:
      "Flywheel enrolled, 5-step Future outreach plan populated. Tests Communication tab right column with plan + feed both rendering — confirms feed has room inside the 680px cap and isn't crushed.",
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-flywheel-with-plan',
      firstName: 'Jordan',
      lastName: 'Webb',
      email: 'jordan.webb@example.com',
      phoneNumber: '+16175550133',
      flywheelEnrolled: true,
      flywheelOptOut: false,
      aiPaused: false,
      claimedLead: null,
      claimedLeadSource: null,
      assignedAgentId: null,
      assignedAgent: null,
      activeHandoffId: null,
      handoffNotes: [],
      ...({ followUpPlan: SAMPLE_FOLLOW_UP_PLAN } as unknown as Partial<Contact>),
    }),
  },
  {
    id: 'flywheel-no-plan',
    label: 'Flywheel · no plan',
    description:
      "Flywheel enrolled, but Future outreach plan has zero steps (renders 'No upcoming touches planned yet'). Tests that the activity feed below still fills the right column and the right column height matches LEFT.",
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-flywheel-no-plan',
      firstName: 'Riley',
      lastName: 'Nakamura',
      email: 'riley.nakamura@example.com',
      phoneNumber: '+16175550144',
      flywheelEnrolled: true,
      flywheelOptOut: false,
      aiPaused: false,
      claimedLead: null,
      claimedLeadSource: null,
      assignedAgentId: null,
      assignedAgent: null,
      activeHandoffId: null,
      handoffNotes: [],
      ...({ followUpPlan: { steps: [], hasActiveFlow: false } } as unknown as Partial<Contact>),
    }),
  },
  {
    id: 'long-opt-in',
    label: 'Long opt-in desc · wrap test',
    description:
      "Opt-in description is a very long URL/string. Tests the Contact Information row — the value should wrap to multiple lines instead of overflowing and overlapping adjacent rows.",
    build: () => ({
      ...FULL_CONTACT_BASE,
      id: 'preview-mock-long-opt-in',
      firstName: 'Morgan',
      lastName: 'Vasquez-Lindgren',
      opt_in: true,
      opt_in_date: '2026-04-01',
      opt_in_desc:
        'https://speculo.example.com/landing-page/zillow-lead-capture-form-with-extremely-long-utm-source-and-campaign-tracking-parameters?utm_source=zillow_premier_agent_q2_campaign&utm_medium=cpc&utm_campaign=south_end_boston_first_time_buyer_remarketing&utm_content=v3_variant_hero_red',
    }),
  },
];

const ContactPreviewPage: React.FC = () => {
  const [scenario, setScenario] = useState<Scenario>('everything');
  const [showToolbar, setShowToolbar] = useState<boolean>(true);
  const active = SCENARIOS.find((s) => s.id === scenario)!;
  const contact = active.build();

  // Route uses PageLayout disablePadding={true} so the bg extends to the top
  // of the viewport; we reapply the topPadding INSIDE the bg div so content
  // still sits below the fixed header.
  const { topPadding } = useLayoutConfig();

  return (
    <div
      className="contact-page-bg h-[100dvh] flex flex-col pb-4"
      style={{ paddingTop: topPadding }}
    >
      <div className="mx-auto max-w-[1700px] 3xl:max-w-[2000px] w-full flex-1 min-h-0 flex flex-col">
        {/* Toolbar — pick a scenario to render. Owns its own horizontal
            margin so it doesn't hug the screen edge (the contact view
            below it handles its own padding internally). Collapsible
            via the toggle on the right so we can see the contact view
            without the visual-QA chrome above it. */}
        {showToolbar ? (
          <div className="mx-3 md:mx-4 mb-4 rounded-xl border border-foreground/15 bg-card/80 backdrop-blur-sm px-4 py-3 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Contact preview · /contact-preview
                  </div>
                  <div className="text-sm font-medium text-foreground mt-0.5">
                    Visual QA · select a scenario
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowToolbar(false)}
                  className="md:hidden text-xs px-2.5 py-1 rounded-md border border-foreground/30 hover:border-foreground/60 text-foreground"
                >
                  Hide
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {SCENARIOS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setScenario(s.id)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      s.id === scenario
                        ? 'bg-foreground text-background border-foreground'
                        : 'bg-card text-foreground border-foreground/30 hover:border-foreground/60'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowToolbar(false)}
                  className="hidden md:inline-flex text-xs px-2.5 py-1.5 rounded-md border border-foreground/30 hover:border-foreground/60 text-foreground ml-2"
                >
                  Hide preview
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-3 md:mx-4 mb-4 flex justify-end">
            <button
              type="button"
              onClick={() => setShowToolbar(true)}
              className="text-xs px-3 py-1.5 rounded-md border border-foreground/30 bg-card/80 backdrop-blur-sm hover:border-foreground/60 text-foreground shadow-sm"
            >
              Show preview
            </button>
          </div>
        )}

        {/* ContactEditContent now fills the remaining viewport — same
            behavior as inside the modal. Internal panels scroll
            independently. */}
        <div className="flex-1 min-h-0 flex flex-col">
          <ContactEditContent
            key={contact.id}
            contact={contact}
            onSubmit={async () => undefined}
            onSuccess={() => undefined}
            defaultTab="activity"
            showHeader={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactPreviewPage;
